"use client";

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "@/lib/auth-context";
import OnboardingFlow from "./OnboardingFlow";

/* Shows a sticky completion prompt when the user has started but not finished onboarding */
export default function OnboardingBanner() {
  const { user } = useAuth();
  const [show,         setShow]         = useState(false);
  const [message,      setMessage]      = useState("");
  const [flowOpen,     setFlowOpen]     = useState(false);
  const [dismissed,    setDismissed]    = useState(false);
  const [mounted,      setMounted]      = useState(false);

  useEffect(() => { setMounted(true); }, []);

  useEffect(() => {
    if (!mounted || dismissed) return;

    const onboarded = localStorage.getItem("blackdoor_onboarded_v1") === "1";
    if (onboarded) { setShow(false); return; }

    const started = localStorage.getItem("blackdoor_onboarding_started_v1") === "1";
    const kycDone = localStorage.getItem("blackdoor_kyc_v1") === "submitted";

    // Only show resume banner if they've touched the flow before
    if (!user || !started) {
      setShow(false);
    } else if (!kycDone) {
      // Logged in but KYC not done
      setMessage("Finish identity verification to start trading");
      setShow(true);
    } else {
      // KYC done but deposit step not acknowledged
      setMessage("Complete setup — fund your account to start trading");
      setShow(true);
    }
  }, [user, mounted, dismissed]);

  if (!mounted) return null;

  return (
    <>
      <AnimatePresence>
        {show && !dismissed && (
          <motion.div
            initial={{ y: -48, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: -48, opacity: 0 }}
            transition={{ type: "spring", stiffness: 380, damping: 32 }}
            className="fixed top-14 inset-x-0 z-40 flex justify-center pointer-events-none px-4"
          >
            <div
              className="pointer-events-auto flex items-center gap-3 px-4 py-2.5 rounded-2xl shadow-xl"
              style={{
                background: "rgba(10,10,20,0.96)",
                border: "1px solid rgba(99,102,241,0.28)",
                backdropFilter: "blur(20px)",
                WebkitBackdropFilter: "blur(20px)",
              }}
            >
              {/* Pulse dot */}
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-indigo-400 opacity-60" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-indigo-400" />
              </span>

              <p className="text-[12px] font-semibold text-white/70 leading-tight max-w-55">{message}</p>

              <button
                onClick={() => setFlowOpen(true)}
                className="shrink-0 px-3 py-1.5 rounded-xl bg-indigo-500 text-white text-[11px] font-black uppercase tracking-[0.06em] hover:bg-indigo-400 transition-colors active:scale-[0.97]">
                Continue
              </button>

              <button
                onClick={() => setDismissed(true)}
                className="shrink-0 h-6 w-6 flex items-center justify-center text-white/25 hover:text-white/55 transition-colors">
                <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                  <path d="M1 1l6 6M7 1L1 7" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                </svg>
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {flowOpen && (
          <OnboardingFlow
            open={flowOpen}
            onClose={() => setFlowOpen(false)}
            onComplete={() => { setShow(false); setFlowOpen(false); }}
          />
        )}
      </AnimatePresence>
    </>
  );
}
